import { useAuthenticationService } from '@/services/authentication/authentication-service';
import { getVariable } from '@/services/backstage/BackstageProvider';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { createContext, FC, PropsWithChildren, useContext, useEffect, useRef } from 'react';

/**
 * One SignalR connection to the rostering hub per user, shared by every open tab.
 *
 * The tab holding a Web Lock (the leader) opens the connection and passes each event to the other tabs over a
 * BroadcastChannel. Optimiser events are sent per location, so every tab tells the leader which location it shows,
 * and the leader joins the group of each location any tab uses. When the leader tab closes, another tab takes over.
 *
 * Use useRealtimeEvent to receive an event and useRealtimeLocation to receive a location's events.
 */

// Events the leader listens for on behalf of every tab.
export const RealtimeEvents = {
  UserNotificationReceived: 'UserNotificationReceived',
  BatchJobStateChanged: 'BatchJobStateChanged'
} as const;
export type RealtimeEvent = (typeof RealtimeEvents)[keyof typeof RealtimeEvents];

type Handler = (payload: any) => void;

type ChannelMessage =
  | { kind: 'event'; event: RealtimeEvent; payload: unknown }
  | { kind: 'locations'; tabId: string; locationIds: number[] }
  | { kind: 'report' }
  | { kind: 'closed'; tabId: string };

interface RealtimeContextValue {
  subscribe: (event: RealtimeEvent, handler: Handler) => () => void;
  addLocation: (locationId: number) => () => void;
}

const RealtimeContext = createContext<RealtimeContextValue | undefined>(undefined);

const RETRY_DELAY_MS = 5000;

const toHubUrl = async (tenantId: string, clientId: string, delegatedSessionId?: string) => {
  const apiUrl = await getVariable('apiUrl');
  if (!apiUrl) {
    throw new Error('apiUrl is not defined.');
  }

  const url = new URL(apiUrl, window.location.origin);
  url.pathname = url.pathname.replace(/\/graphql(?:\.generated)?$/, '/hubs/rostering-realtime');
  url.searchParams.set('tenant-id', tenantId);
  url.searchParams.set('client-id', clientId);
  if (delegatedSessionId) {
    url.searchParams.set('x-delegation-session-id', delegatedSessionId);
  }
  return url.toString();
};

const waitOrAbort = (ms: number, signal: AbortSignal) =>
  new Promise<void>(resolve => {
    const timer = window.setTimeout(resolve, ms);
    signal.addEventListener('abort', () => {
      window.clearTimeout(timer);
      resolve();
    });
  });

export const RosteringRealtimeProvider: FC<PropsWithChildren> = ({ children }) => {
  const [accessToken, tenantId, delegatedSessionId] = useAuthenticationService(state => [
    state.user?.access_token,
    state.getEffectiveTenantId(),
    state.delegatedSession?.delegationSessionId
  ]);
  const hasToken = !!accessToken;
  const accessTokenRef = useRef(accessToken);

  // A refreshed token is picked up on the next (re)connect, without dropping the connection.
  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  // Kept across reconnects so subscribers and locations don't need to register again.
  const handlers = useRef(new Map<RealtimeEvent, Set<Handler>>());
  const locationCounts = useRef(new Map<number, number>());
  const announceRef = useRef<() => void>(() => undefined);

  const dispatch = (event: RealtimeEvent, payload: unknown) => {
    handlers.current.get(event)?.forEach(handler => handler(payload));
  };

  const contextValue = useRef<RealtimeContextValue>({
    subscribe: (event, handler) => {
      const set = handlers.current.get(event) ?? new Set<Handler>();
      set.add(handler);
      handlers.current.set(event, set);
      return () => set.delete(handler);
    },
    addLocation: locationId => {
      locationCounts.current.set(locationId, (locationCounts.current.get(locationId) ?? 0) + 1);
      announceRef.current();
      return () => {
        const count = (locationCounts.current.get(locationId) ?? 1) - 1;
        if (count > 0) {
          locationCounts.current.set(locationId, count);
        } else {
          locationCounts.current.delete(locationId);
        }
        announceRef.current();
      };
    }
  }).current;

  useEffect(() => {
    if (!hasToken || !tenantId) {
      return;
    }

    const tabId = crypto.randomUUID();
    const name = `rostering-realtime:${tenantId}:${delegatedSessionId ?? ''}`;
    const channel = new BroadcastChannel(name);
    const abort = new AbortController();
    const aborted = new Promise<void>(resolve => abort.signal.addEventListener('abort', () => resolve()));

    const myLocations = () => [...locationCounts.current.keys()];

    // Only set while this tab is the leader.
    let leader: { tabLocations: Map<string, number[]>; sync: () => void } | undefined;

    const announce = () => {
      if (leader) {
        leader.tabLocations.set(tabId, myLocations());
        leader.sync();
      } else {
        channel.postMessage({ kind: 'locations', tabId, locationIds: myLocations() } satisfies ChannelMessage);
      }
    };
    announceRef.current = announce;

    channel.onmessage = ({ data }: MessageEvent<ChannelMessage>) => {
      switch (data.kind) {
        case 'event':
          dispatch(data.event, data.payload);
          break;
        case 'report':
          announce();
          break;
        case 'locations':
          leader?.tabLocations.set(data.tabId, data.locationIds);
          leader?.sync();
          break;
        case 'closed':
          leader?.tabLocations.delete(data.tabId);
          leader?.sync();
          break;
      }
    };

    const onPageHide = () => channel.postMessage({ kind: 'closed', tabId } satisfies ChannelMessage);
    window.addEventListener('pagehide', onPageHide);

    // Runs only in the tab holding the lock, until this provider unmounts.
    const lead = async () => {
      const hubUrl = await toHubUrl(tenantId, (await getVariable('authIdentityClientId')) || '', delegatedSessionId);
      const tabLocations = new Map<string, number[]>([[tabId, myLocations()]]);
      let connection: HubConnection | undefined;
      let joined = new Set<number>();

      // Joins and leaves location groups so the connection is in exactly the ones some tab uses.
      const sync = async () => {
        const wanted = new Set([...tabLocations.values()].flat());
        if (!connection || connection.state !== HubConnectionState.Connected) {
          return;
        }
        const current = connection;
        const toJoin = [...wanted].filter(id => !joined.has(id));
        const toLeave = [...joined].filter(id => !wanted.has(id));
        joined = wanted;
        await Promise.all([
          ...toJoin.map(id => current.invoke('JoinLocationGroup', id)),
          ...toLeave.map(id => current.invoke('LeaveLocationGroup', id))
        ]).catch(error => console.warn('Failed to update realtime location groups.', error));
      };

      leader = { tabLocations, sync: () => void sync() };
      channel.postMessage({ kind: 'report' } satisfies ChannelMessage);

      while (!abort.signal.aborted) {
        connection = new HubConnectionBuilder()
          .withUrl(hubUrl, { accessTokenFactory: () => accessTokenRef.current ?? '', withCredentials: false })
          .withAutomaticReconnect()
          .configureLogging(LogLevel.Warning)
          .build();

        Object.values(RealtimeEvents).forEach(event =>
          connection!.on(event, (payload: unknown) => {
            dispatch(event, payload);
            channel.postMessage({ kind: 'event', event, payload } satisfies ChannelMessage);
          })
        );
        // A reconnect gets a new connection id, which is in no group yet.
        connection.onreconnected(() => {
          joined = new Set();
          void sync();
        });
        const closed = new Promise<void>(resolve => connection!.onclose(() => resolve()));

        try {
          await connection.start();
        } catch {
          await waitOrAbort(RETRY_DELAY_MS, abort.signal);
          continue;
        }

        joined = new Set();
        await sync();

        // Automatic reconnect covers short drops; onclose means it gave up, so build a new connection.
        await Promise.race([closed, aborted]);
        if (abort.signal.aborted) {
          await connection.stop();
        }
      }
      leader = undefined;
    };

    // Rejects with AbortError if this provider unmounts while still waiting for the lock.
    navigator.locks.request(name, { signal: abort.signal }, lead).catch(() => undefined);
    announce();

    return () => {
      abort.abort();
      onPageHide();
      window.removeEventListener('pagehide', onPageHide);
      channel.close();
      announceRef.current = () => undefined;
    };
  }, [hasToken, tenantId, delegatedSessionId]);

  return <RealtimeContext.Provider value={contextValue}>{children}</RealtimeContext.Provider>;
};

/** Calls handler for each event of this type, in every open tab. Does nothing outside RosteringRealtimeProvider. */
export const useRealtimeEvent = (event: RealtimeEvent, handler: Handler) => {
  const context = useContext(RealtimeContext);
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => context?.subscribe(event, payload => handlerRef.current(payload)), [context, event]);
};

/** Makes sure events sent to this location's group are received while the calling component is mounted. */
export const useRealtimeLocation = (locationId: number | undefined) => {
  const context = useContext(RealtimeContext);

  useEffect(() => {
    if (!context || locationId === undefined) {
      return;
    }
    return context.addLocation(locationId);
  }, [context, locationId]);
};
